/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  BadgeType,
  Asset,
  TvAssetUtils,
  RelativeTime,
  Badge,
  Program,
  ParentalRatingOptionsType,
  Icons,
  RecordingsUtils,
  RecordingStatus,
  ContentTagsUtils,
  AssetUtils,
  TvAsset,
  Ducks,
  DisabledFeaturesValues,
  TimeUtils,
  Channel,
} from '@wit/vtv-x-foundation-lib';
import store from 'store/store';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import ParentalControl from '../ParentalControl';
import { Selectors as SeriesRecordingsSelectors } from '../../../store/ducks/recordings/seriesRecordings';
import { Selectors as SingleRecordingsSelectors } from '../../../store/ducks/recordings/singleRecordings';
import BadgeEngine from './BadgeEngine';
import Colors from '../../../../ui/common/Colors';

export default class CLUBadgeEngine extends BadgeEngine {
  private static BADGE_PRIORITY = {
    LIVE_EVENT: [
      BadgeType.LOCK,
      BadgeType.SERIES_RECORDING,
      BadgeType.SINGLE_RECORDING,
      BadgeType.RESTART,
      BadgeType.PLAY,
      BadgeType.DISCOUNT,
      BadgeType.PURCHASE,
    ],
    PAST_EVENT: [BadgeType.LOCK, BadgeType.PLAY, BadgeType.DISCOUNT, BadgeType.PURCHASE],
    FUTURE_EVENT: [
      BadgeType.LOCK,
      BadgeType.SERIES_SCHEDULED_RECORDING,
      BadgeType.SINGLE_SCHEDULED_RECORDING,
      BadgeType.DISCOUNT,
      BadgeType.PURCHASE,
    ],
  };

  private static getPriority(asset: Asset): BadgeType[] {
    if (!AssetUtils.isTvAsset(asset)) {
      return [];
    }

    switch (TvAssetUtils.getRelativeTime(asset)) {
      case RelativeTime.PAST:
        return this.BADGE_PRIORITY.PAST_EVENT;
      case RelativeTime.LIVE:
        return this.BADGE_PRIORITY.LIVE_EVENT;
      case RelativeTime.FUTURE:
        return this.BADGE_PRIORITY.FUTURE_EVENT;
      default:
        return [];
    }
  }

  private static getBadgesForCLUProgram(asset: Asset): Asset {
    // Program with ID = -1 it's a filler
    if (!AssetUtils.isEpg(asset) || asset.id === -1) {
      return asset;
    }

    const priority = CLUBadgeEngine.getPriority(asset);
    let badge: Badge | undefined;

    for (let i = 0; badge === undefined && i < priority.length; i += 1) {
      badge = CLUBadgeEngine.determineBadge(priority[i], asset);
    }

    if (!badge) {
      return asset;
    }

    return {
      ...asset,
      badging: badge,
    };
  }

  private static getSlotsPriority(asset: TvAsset): BadgeType[][] {
    switch (TvAssetUtils.getRelativeTime(asset)) {
      case RelativeTime.LIVE:
        return BADGES_PRIORITY_PER_SECTION_RAIL.WATCH_TV.ON_NOW;
      case RelativeTime.FUTURE:
      case RelativeTime.PAST:
        return BADGES_PRIORITY_PER_SECTION_RAIL.WATCH_TV.FUTURE_AND_PAST;
      default:
        return [];
    }
  }

  private static getBadgesListForProgram(asset: Program): Program {
    // Program with ID = -1 it's a filler
    if (!AssetUtils.isEpg(asset) || asset.id === -1) {
      return asset;
    }

    const priority = CLUBadgeEngine.getSlotsPriority(asset);
    const badgesList: Badge[] = [];

    for (let i = 0; i < priority.length; i += 1) {
      const slotBadgesPriority = priority[i];

      for (let ii = 0; ii < slotBadgesPriority.length; ii += 1) {
        const badge = CLUBadgeEngine.determineBadge(slotBadgesPriority[ii], asset);

        if (badge) {
          badgesList.push(badge);
          break;
        }
      }
    }

    return {
      ...asset,
      badges: badgesList,
    };
  }

  static getAssetsWithBadgesList(assets: Program[]): Program[] {
    const result: Program[] = [];
    assets.forEach((asset) => result.push(CLUBadgeEngine.getBadgesListForProgram(asset)));
    return result;
  }

  static getAssetsWithBadgesFromCLU(assets: Asset[]): Asset[] {
    const result: Asset[] = [];
    assets.forEach((asset) => result.push(CLUBadgeEngine.getBadgesForCLUProgram(asset)));
    return result;
  }

  private static determineBadge(type: BadgeType, program: Program): Badge | undefined {
    switch (type) {
      case BadgeType.LOCK:
        return CLUBadgeEngine.determineLockBadge(program);
      case BadgeType.SERIES_RECORDING:
      case BadgeType.SERIES_SCHEDULED_RECORDING:
        return CLUBadgeEngine.determineSeriesRecordingBadge(program);
      case BadgeType.SINGLE_RECORDING:
      case BadgeType.SINGLE_SCHEDULED_RECORDING:
        return CLUBadgeEngine.determineSingleRecordingBadge(program);
      case BadgeType.RECORDABLE:
        return CLUBadgeEngine.determineRecordableBadge(program);
      case BadgeType.RESTART:
        return CLUBadgeEngine.determineRestartBadge(program);
      case BadgeType.PLAY:
        return CLUBadgeEngine.determinePlayBadge(program);
      case BadgeType.PURCHASE:
        return CLUBadgeEngine.determinePurchaseBadge(program);
      default:
        return undefined;
    }
  }

  private static determineLockBadge(program: Program): Badge | undefined {
    if (ParentalControl.UI.getAssetParentalState(program) === ParentalRatingOptionsType.PIN) {
      return {
        type: BadgeType.LOCK,
        icon: Icons.LOCK,
      };
    }
    return undefined;
  }

  private static determineRecordableBadge(program: Program): Badge | undefined {
    const isRecordingsDisabled = Ducks.Dms.Selectors.isFeatureDisabled(
      store().getState(),
      DisabledFeaturesValues.NO_PVR
    );

    const channel = CLUBadgeEngine.getAssetEntitled(program) as Channel;

    const contentTagsAllowRecordings = ContentTagsUtils.isRecordingEnabled(program);

    if (isRecordingsDisabled || !TimeUtils.isAfterNow(program.endDate) || !channel || !contentTagsAllowRecordings) {
      return undefined;
    }

    return {
      type: BadgeType.RECORDABLE,
      icon: Icons.RECORDINGS,
    };
  }

  private static determineSeriesRecordingBadge(program: Program): Badge | undefined {
    const seriesRecordings = SeriesRecordingsSelectors.getRecordings(store().getState());
    const serieRecording = seriesRecordings.find((e) => e.seriesId === program.seriesId);

    if (serieRecording) {
      const recordings = SingleRecordingsSelectors.getRecordings(store().getState());
      const recording = recordings.find((e) => e.internalId === program.internalId);

      if (recording) {
        if (
          (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.ON_GOING &&
            TvAssetUtils.getRelativeTime(recording) === RelativeTime.LIVE) ||
          TvAssetUtils.getRelativeTime(program) === RelativeTime.LIVE
        ) {
          return {
            type: BadgeType.SERIES_RECORDING,
            icon: Icons.RECORDING_SERIES,
            color: Colors.brand,
          };
        }
      }

      return {
        type: BadgeType.SERIES_SCHEDULED_RECORDING,
        icon: Icons.RECORDING_SERIES_SCHEDULED,
      };
    }

    return undefined;
  }

  private static determineSingleRecordingBadge(program: Program): Badge | undefined {
    const recordings = SingleRecordingsSelectors.getRecordings(store().getState());
    const recording = recordings.find((e) => e.internalId === program.internalId);

    if (recording) {
      if (
        (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.ON_GOING &&
          TvAssetUtils.getRelativeTime(recording) === RelativeTime.LIVE) ||
        TvAssetUtils.getRelativeTime(program) === RelativeTime.LIVE
      ) {
        return {
          type: BadgeType.SINGLE_RECORDING,
          icon: Icons.RECORDING_SINGLE,
          color: Colors.brand,
        };
      }

      if (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.SCHEDULED) {
        return {
          type: BadgeType.SINGLE_SCHEDULED_RECORDING,
          icon: Icons.RECORDING_SINGLE_SCHEDULED,
        };
      }
    }

    return undefined;
  }

  private static determineRestartBadge(program: Program): Badge | undefined {
    if (CLUBadgeEngine.getAssetEntitled(program) && ContentTagsUtils.isResartAllowedForEpgAsset(program)) {
      return {
        type: BadgeType.RESTART,
        icon: Icons.RESTART,
      };
    }

    return undefined;
  }

  private static determinePlayBadge(program: Program): Badge | undefined {
    if (CLUBadgeEngine.getAssetEntitled(program)) {
      if (ContentTagsUtils.isResartAllowedForEpgAsset(program)) {
        return {
          type: BadgeType.PLAY,
          icon: Icons.PLAY_CIRCLE,
        };
      }

      const recordings = SingleRecordingsSelectors.getRecordings(store().getState());
      const recording = recordings.find((e) => e.internalId === program.internalId);

      if (
        recording &&
        RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.COMPLETED &&
        ContentTagsUtils.isPlaybackAllowedForRecording(recording)
      ) {
        return {
          type: BadgeType.PLAY,
          icon: Icons.PLAY_CIRCLE,
        };
      }
    }

    return undefined;
  }

  private static determinePurchaseBadge(program: Program): Badge | undefined {
    if (CLUBadgeEngine.isPurchasedBadgeEnabled() && !CLUBadgeEngine.getAssetEntitled(program)) {
      return {
        type: BadgeType.PURCHASE,
        icon: CLUBadgeEngine.getCurrencyIcon(),
      };
    }

    return undefined;
  }
}
